import { z } from 'zod';

import { foldDigitsToAscii } from '@shared/format';

import { authCopy } from './copy';
import { CODE_MAX_LENGTH, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, PHONE_MAX_LENGTH } from './constants';

/**
 * The three field shapes all three screens draw. Each page composes its own
 * `forms.schema.ts` from these; the third occurrence is what earned the
 * extraction, and none of them crosses out of this feature.
 *
 * **Digits fold before anything else runs.** An operator on an Arabic keyboard
 * types `٧٧٠…`, and a validator that rejects that glyph rejects the operator
 * for their own keyboard layout. The fold is the shared one in
 * `@shared/format/digits.ts` — the same implementation the rest of the console
 * uses — and it runs inside the transform so the *submitted* value is ASCII
 * while what stays in the field is whatever was typed.
 *
 * The server revalidates every one of these. `backend-auth` normalises the
 * number to E.164 at its own edge and refuses with `AUTH_PHONE_INVALID`
 * whatever this schema does, so the rules below buy an earlier answer, never a
 * guarantee.
 */

/**
 * A supported mobile, in every shape `backend-auth`'s `to_e164` accepts: the Yemeni
 * `+9677xxxxxxxx`, its `009677xxxxxxxx` spelling, the same number with the country
 * code but no `+`, and the bare `7xxxxxxxx` most people give when asked for their own
 * number. Separators are already gone by the time this runs.
 *
 * **The retired Iraqi market is not here, and its absence is the point.** `+9647…` /
 * `07xxxxxxxxx` are shapes the service refuses, so a client that accepted them would
 * send the operator round a trip to be told no.
 *
 * A leading `0` is never accepted as a Yemeni number: it is a trunk prefix in markets
 * Shahedni does not serve, and reading it as Yemeni would rewrite one market's number
 * under another market's code.
 *
 * The pattern is the loose half of the server's rule on purpose — a client that guessed
 * a *tighter* rule than the service would refuse numbers the platform accepts, which is
 * the worse failure of the two.
 */
const SUPPORTED_MOBILE = /^(?:(?:\+|00)?967)?7\d{8}$/;

export const phoneField = z
  .string()
  .max(PHONE_MAX_LENGTH, authCopy.field.phoneInvalid)
  .transform((value) => foldDigitsToAscii(value).replace(/[\s\-()]/g, ''))
  .pipe(z.string().min(1, authCopy.field.phoneRequired).regex(SUPPORTED_MOBILE, authCopy.field.phoneInvalid));

/**
 * The one-time code. **No length is fixed here.** `OTP_CODE_LENGTH` is a served
 * setting, so a page that needs to check the length reads it and pipes this
 * shape through `withCodeLength`; a literal here would compile in a value the
 * catalog does not declare and the console could not follow when it changed.
 */
export const codeField = z
  .string()
  .transform(foldDigitsToAscii)
  .pipe(
    z
      .string()
      .min(1, authCopy.field.codeRequired)
      .max(CODE_MAX_LENGTH, authCopy.field.codeTooLong)
      .regex(/^\d+$/, authCopy.field.codeRequired),
  );

/**
 * Narrows the code to the length the service currently issues. The value comes
 * from `GET /api/v1/authConfiguration`; where the read has not resolved, the
 * unnarrowed shape is used and the server is left to answer.
 */
export function withCodeLength(length: number | undefined) {
  if (!length || length <= 0) return codeField;
  return codeField.pipe(z.string().length(length, authCopy.field.codeRequired));
}

export const passwordField = z
  .string()
  .min(1, authCopy.field.passwordRequired)
  .min(PASSWORD_MIN_LENGTH, authCopy.field.passwordTooShort)
  .max(PASSWORD_MAX_LENGTH, authCopy.field.passwordTooLong);
